/**
 * Signing in to the platform admin, and staying signed in.
 *
 * Always two steps. A right password gives a challenge, never a session: the
 * session comes from a code on the operator's phone. The first time, there is
 * no app yet, so the second step is setting one up — scan, type the first six
 * digits, keep the recovery codes — and only then does the app open. There is
 * no path around it here because there is none on the server.
 *
 * An emailed link (`?reset=…`) opens the set-password form instead: it is how
 * a new operator gets their first password, and how a forgotten one is
 * replaced.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  ApiError,
  PASSWORD_MIN,
  operatorSession,
  useApi,
  useBeginOperatorSetup,
  useConfirmOperatorSetup,
  useOperator,
  useOperatorCode,
  useOperatorForgotPassword,
  useOperatorResetPassword,
  useOperatorSignIn,
  useRenewOperatorSession,
  useSession,
  useStartSession,
  type OperatorChallenge,
  type OperatorSetupDone,
} from '@restaurant/api-client';
import { Button, ErrorNotice, Field, Input, Loading, SignInForm } from '@restaurant/ui';

import { QrSquare, RecoveryCodes } from './authenticator';
import { inFours, shouldRenew } from './words';

export function Shell({ children }: { children: ReactNode }) {
  const session = useSession();
  // Read once, and taken out of the address bar when used or left, so the
  // link is not in the history for the next person at this browser.
  const [resetToken, setResetToken] = useState(readResetToken);

  if (!session.isAuthenticated) {
    if (resetToken) {
      return (
        <SetPasswordForm
          token={resetToken}
          onDone={() => {
            window.history.replaceState(null, '', window.location.pathname);
            setResetToken(null);
          }}
        />
      );
    }
    return <SignedOut />;
  }
  return <SignedIn>{children}</SignedIn>;
}

function readResetToken(): string | null {
  return new URLSearchParams(window.location.search).get('reset');
}

function SignedOut() {
  const signIn = useOperatorSignIn();
  const code = useOperatorCode();
  const start = useStartSession();
  const [forgot, setForgot] = useState(false);
  const [done, setDone] = useState<OperatorSetupDone | null>(null);

  if (done) {
    return (
      <Frame
        eyebrow="Platform admin"
        title="Your recovery codes"
        lead="Your authenticator app is set up. Save these before you go on."
        foot={
          <Button
            variant="primary"
            size="lg"
            block
            onClick={() => start(operatorSession(done))}
          >
            I have saved them
          </Button>
        }
      >
        <RecoveryCodes codes={done.recovery_codes} />
      </Frame>
    );
  }

  if (forgot) return <ForgotForm onBack={() => setForgot(false)} />;

  const challenge = signIn.data;
  if (challenge?.setup_required) {
    return (
      <SetUpAuthenticator
        challenge={challenge}
        onDone={setDone}
        onRestart={() => {
          signIn.reset();
          code.reset();
        }}
      />
    );
  }

  return (
    <SignInForm
      appName="Platform admin"
      eyebrow="OnRestaurant"
      tagline="For the people who run the platform: restaurants, plans and invoices."
      askTenant={false}
      submitting={signIn.isPending}
      error={signIn.error ? signInMessage(signIn.error) : null}
      onSubmit={({ email, password }) => signIn.mutate({ email, password })}
      result={challenge}
      onSubmitCode={(challengeToken, typed) => code.mutate({ challengeToken, code: typed })}
      codeSubmitting={code.isPending}
      codeError={code.error}
      onRestart={() => {
        signIn.reset();
        code.reset();
      }}
      alternative={
        <button type="button" className="signin__alt" onClick={() => setForgot(true)}>
          Forgot your password?
        </button>
      }
    />
  );
}

function signInMessage(error: ApiError): string {
  if (error.status === 401) return 'Incorrect email or password.';
  if (error.status === 429) return 'Too many tries. Wait a few minutes and try again.';
  if (error.code === 'network_unreachable') {
    return 'Cannot reach the server. Check the connection and try again.';
  }
  return error.message;
}

/** The sign-in card's frame, for the steps the shared form does not draw. */
function Frame({
  eyebrow,
  title,
  lead,
  children,
  foot,
  onSubmit,
}: {
  eyebrow: string;
  title: string;
  lead: string;
  children: ReactNode;
  foot: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <main className="signin">
      <form
        className="signin__card"
        onSubmit={onSubmit ?? ((e) => e.preventDefault())}
        noValidate
      >
        <div className="signin__body">
          <header className="signin__head">
            <p className="signin__eyebrow">{eyebrow}</p>
            <h1 className="signin__title">{title}</h1>
            <p className="signin__tagline">{lead}</p>
          </header>
          {children}
        </div>
        <div className="signin__foot">{foot}</div>
      </form>
    </main>
  );
}

function SetUpAuthenticator({
  challenge,
  onDone,
  onRestart,
}: {
  challenge: OperatorChallenge;
  onDone: (done: OperatorSetupDone) => void;
  onRestart: () => void;
}) {
  const begin = useBeginOperatorSetup();
  const confirm = useConfirmOperatorSetup();
  const [code, setCode] = useState('');
  const { mutate: beginSetup } = begin;
  const typed = code.replace(/\s+/g, '');

  // The secret is made as soon as this step opens: there is nothing to decide
  // first, and an operator cannot go anywhere else until it is done.
  useEffect(() => {
    beginSetup({ challengeToken: challenge.challenge_token });
  }, [beginSetup, challenge.challenge_token]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (typed === '') return;
    confirm.mutate(
      { challengeToken: challenge.challenge_token, code: typed },
      { onSuccess: onDone },
    );
  }

  const expired = begin.error?.status === 401 || confirm.error?.status === 429;
  return (
    <Frame
      eyebrow="Platform admin"
      title="Set up your authenticator app"
      lead="Every sign-in here asks for a code from an app on your phone. Nothing opens until it is set up."
      onSubmit={submit}
      foot={
        <>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            disabled={typed === '' || !begin.data}
            loading={confirm.isPending}
          >
            Turn it on and sign in
          </Button>
          <button type="button" className="signin__alt" onClick={onRestart}>
            Back to your password
          </button>
        </>
      }
    >
      {begin.isPending && <Loading label="Making your key" />}
      {begin.error && (
        <p className="signin__error" role="alert">
          {expired
            ? 'That sign-in has expired. Go back and enter your password again.'
            : begin.error.message}
        </p>
      )}
      {begin.data && (
        <>
          <p className="pf__sub">
            Open an authenticator app, add an account, and point it at this square.
          </p>
          <QrSquare uri={begin.data.provisioning_uri} />
          <p className="pf__sub">If the camera will not read it, type this key into the app:</p>
          <p className="pf__secret">{inFours(begin.data.secret)}</p>
          <Field
            label="The six digits the app shows"
            error={
              confirm.error?.status === 401
                ? 'That code is not right. Wait for the next one and try again.'
                : confirm.error?.status === 429
                  ? 'Too many tries. Wait a few minutes, then start again.'
                  : confirm.error
                    ? confirm.error.message
                    : undefined
            }
          >
            <Input
              className="pf__code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
          </Field>
        </>
      )}
    </Frame>
  );
}

function ForgotForm({ onBack }: { onBack: () => void }) {
  const forgot = useOperatorForgotPassword();
  const [email, setEmail] = useState('');
  const ready = email.includes('@');

  return (
    <Frame
      eyebrow="Platform admin"
      title="Forgot your password?"
      lead={
        forgot.isSuccess
          ? `If ${email.trim()} is an operator's address, a link to set a new password is on its way. It works once, for half an hour.`
          : 'We will email you a link to set a new one. Your authenticator app stays as it is.'
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) forgot.mutate({ email: email.trim() });
      }}
      foot={
        <>
          {!forgot.isSuccess && (
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              disabled={!ready}
              loading={forgot.isPending}
            >
              Email me a link
            </Button>
          )}
          <button type="button" className="signin__alt" onClick={onBack}>
            Back to sign in
          </button>
        </>
      }
    >
      {!forgot.isSuccess && (
        <Field label="Your email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
        </Field>
      )}
      {forgot.error && (
        <p className="signin__error" role="alert">
          {forgot.error.message}
        </p>
      )}
    </Frame>
  );
}

function SetPasswordForm({ token, onDone }: { token: string; onDone: () => void }) {
  const reset = useOperatorResetPassword();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const short = password.length > 0 && password.length < PASSWORD_MIN;
  const differ = again.length > 0 && again !== password;
  const ready = password.length >= PASSWORD_MIN && again === password;

  return (
    <Frame
      eyebrow="Platform admin"
      title={reset.isSuccess ? 'Password set' : 'Set your password'}
      lead={
        reset.isSuccess
          ? 'Sign in with it now. You will be asked for a code from your authenticator app, or to set one up.'
          : 'Choose the password you will sign in to the platform admin with.'
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) reset.mutate({ token, new_password: password });
      }}
      foot={
        reset.isSuccess ? (
          <Button variant="primary" size="lg" block onClick={onDone}>
            Sign in
          </Button>
        ) : (
          <>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              disabled={!ready}
              loading={reset.isPending}
            >
              Set password
            </Button>
            <button type="button" className="signin__alt" onClick={onDone}>
              Back to sign in
            </button>
          </>
        )
      }
    >
      {!reset.isSuccess && (
        <>
          <Field
            label="New password"
            hint={`At least ${PASSWORD_MIN} characters. A few unrelated words is easier to remember than symbols.`}
            error={
              short
                ? `${PASSWORD_MIN - password.length} more character${PASSWORD_MIN - password.length === 1 ? '' : 's'} needed.`
                : undefined
            }
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <Field label="The same again" error={differ ? 'The two do not match.' : undefined}>
            <Input
              type="password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
        </>
      )}
      {reset.error && (
        <p className="signin__error" role="alert">
          {reset.error.message}
        </p>
      )}
    </Frame>
  );
}

function SignedIn({ children }: { children: ReactNode }) {
  const operator = useOperator();
  useKeepSignedIn();

  if (operator.isPending) return <Loading label="Signing you in" />;
  if (operator.isError) {
    return (
      <div className="pf__main">
        <ErrorNotice title="Could not load your login" message={operator.error.message} />
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Renew the half-hour pass while somebody is using the app, never past the
 * working day the server allows; an idle tab is left to run out
 * (`shouldRenew`). When the server refuses, the client's ordinary "session
 * over" path brings the sign-in back.
 */
function useKeepSignedIn(): void {
  const api = useApi();
  const renew = useRenewOperatorSession();
  const lastActivity = useRef(Date.now());
  const mutate = useRef(renew.mutate);
  const pending = useRef(false);
  mutate.current = renew.mutate;
  pending.current = renew.isPending;

  useEffect(() => {
    const touched = () => {
      lastActivity.current = Date.now();
    };
    window.addEventListener('pointerdown', touched);
    window.addEventListener('keydown', touched);
    const timer = window.setInterval(() => {
      const tokens = api.tokens.get();
      if (!tokens || pending.current) return;
      if (
        shouldRenew({
          expiresAt: tokens.expiresAt,
          lastActivityAt: lastActivity.current,
          now: Date.now(),
        })
      ) {
        mutate.current();
      }
    }, 30_000);
    return () => {
      window.removeEventListener('pointerdown', touched);
      window.removeEventListener('keydown', touched);
      window.clearInterval(timer);
    };
  }, [api]);
}
